import { useState } from 'react';
import type { FetchUserRoleAssignmentsResponse, UserRoleAssignment } from 'vopay';
import vopayApi from '@/services/api.config';

const { api } = vopayApi;

// Backend encodes the response with JSON_FORCE_OBJECT, so:
//   - an empty Roles list arrives as `{}` (not `[]`)
//   - a populated one arrives as `{ "0": {...}, "1": {...} }`
//   - inner Permissions arrive as `{ "0": "23", "1": "24" }` (numeric IDs
//     serialised as strings)
//   - RoleID itself comes back as a string ("35") rather than a number
// Normalise both shape (object→array) and primitive types here so the rest
// of the FE can compare RoleIDs strictly and iterate Permissions cleanly.
function normalizeRoles(raw: unknown): UserRoleAssignment[] {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw as Record<string, unknown>);
  return list.map((r) => {
    const role = r as { RoleID: string | number; Name: string; Permissions?: unknown };
    const permsRaw = role.Permissions;
    const permList: Array<string | number> = permsRaw
      ? Array.isArray(permsRaw)
        ? (permsRaw as Array<string | number>)
        : (Object.values(permsRaw as Record<string, string | number>) as Array<string | number>)
      : [];
    return {
      RoleID: typeof role.RoleID === 'string' ? parseInt(role.RoleID, 10) : role.RoleID,
      Name: role.Name,
      Permissions: permList.map((p) => (typeof p === 'string' ? parseInt(p, 10) : p)),
    };
  });
}

/**
 * Direct Pattern hook for fetching the Account-level Roles assigned to a
 * specific user. Hits GET /account/user-roles/user?Username=<email>.
 *
 * Accepts an optional `bearer` so the same drawer can be reused under a
 * sub-account auth context (e.g. from SubAccounts → User Management) without
 * leaking the parent-account JWT into the request.
 *
 * Distinct from `useFetchUserRoles` (Transaction Approval workflow roles)
 * and `useFetchAccountUserRoles` (the full list of Roles on the account).
 */
export default function useFetchUserRoleAssignments(bearer?: string) {
  const [fetching, setFetching] = useState(false);
  const [roles, setRoles] = useState<UserRoleAssignment[]>([]);

  async function fetchUserRoleAssignments(
    username: string,
  ): Promise<FetchUserRoleAssignmentsResponse> {
    // Clear stale state from the previous fetch so a failure here can't leave
    // the previous user's roles visible to the next caller.
    setRoles([]);
    try {
      setFetching(true);
      const res = await api.fetchUserRoleAssignments({ Username: username }, undefined, bearer);
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      setRoles(normalizeRoles(res.Roles));
      return res;
    } catch (error) {
      console.error('Fetch user role assignments failed:', error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  function reset() {
    setRoles([]);
  }

  return { fetching, roles, fetchUserRoleAssignments, reset };
}
